import { useEffect, useRef, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from './AuthContext';

type RequireAuthProps = PropsWithChildren<{
  loadingComponent?: React.ReactNode;
}>;

export function RequireAuth({
  children,
  loadingComponent,
}: Readonly<RequireAuthProps>) {
  const { isAuthenticated, isSigningOut, signIn } = useAuth();
  const location = useLocation();
  const signInStarted = useRef(false);

  useEffect(() => {
    // Guard against React strictMode double invocation.
    // OIDC sign in redirects should only be initiated once per RequireAuth mount.
    // In StrictMode (development), effects run twice so the second call would start a
    // duplicate OIDC discovery flow that safari and other browsers may abort,
    // resulting in a load failed network error on the openid configuration endpoint.
    if (signInStarted.current) return;

    // Skip the sign-in redirect while a sign-out is in flight, otherwise the
    // resulting navigate to /authorize cancels the /endsession navigation and
    // we end up looping back into the app still signed in.
    if (!isAuthenticated && !isSigningOut) {
      signInStarted.current = true;

      // Start sign-in process with current location
      // - This causes a redirect to the identity provider, and after successful
      //   sign-in the user is redirected back to the app to Callback route
      //   and state contains the same location they were trying to access
      //   before signing in
      signIn({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state:
          location.state && typeof location.state === 'object'
            ? location.state
            : {},
      });
    }
  }, [isAuthenticated, isSigningOut, signIn, location]);

  if (!isAuthenticated) {
    return loadingComponent ?? null;
  }

  return <>{children}</>;
}
